import clsx from "clsx";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import millify from "millify";
import { Tooltip } from "react-tooltip";

import { formatMoney, getDashboardChartData } from "helpers";
import LoadingContent from "components/common/LoadingContent";
import MultipleLineChart from "components/common/MultipleLineChart";
import { SpaceForecastsTabContentProps } from "./types/SpaceForecastsTabProps";

export default function SpaceDetailsForecastTabSingleGraphView(
    props: SpaceForecastsTabContentProps
) {
    const { singleGwmData: gwmData, singleGwmLoading: gwmLoading, filter } = props;

    const [chartData, setChartData] = useState<{ [key: string]: any }>([]);

    useEffect(() => {
        if (gwmData) {
            let chartDataValues;
            switch (filter?.period) {
                case "custom":
                    if (filter?.startDate && filter?.endDate) {
                        chartDataValues = getDashboardChartData(gwmData, "custom", [
                            filter?.startDate,
                            filter?.endDate,
                        ]);
                    } else {
                        return;
                    }
                    break;
                default:
                    chartDataValues = getDashboardChartData(gwmData, filter?.period);
            }

            setChartData(chartDataValues);
        }
    }, [gwmData, filter?.period, filter?.startDate, filter?.endDate]);

    const productData = [1];

    const forecastMetrics = [
        {
            title: "Forecasted revenue",
            values: [
                {
                    title: "Configured revenue forecast",
                    value: `$${
                        Boolean(gwmData?.configuredRevForecast) &&
                        gwmData?.configuredRevForecast !== undefined
                            ? millify(Number(gwmData?.configuredRevForecast), {
                                  precision: 2,
                              })
                            : "----"
                    }`,
                },
                {
                    title: "Synth’s revenue forecast",
                    value:
                        "$" + gwmData?.forecastTotalSale !== undefined
                            ? millify(Number(gwmData?.forecastTotalSale), {
                                  precision: 2,
                              })
                            : "----",
                },
            ],
            toolTipContent: {
                title: "Synth’s revenue forecast",
                content:
                    "This is the total amount our system has forecasted you would sell for all your bundles over the next 6 months. We use the current unit bundle value/amount multiplied by the forecasted quantity.",
            },
        },
        {
            title: "Forecasted sales quantity",
            values: [
                {
                    title: "Configured demand forecast",
                    value: `$${
                        gwmData?.configuredDemandForecast
                            ? formatMoney().format(gwmData?.configuredDemandForecast)
                            : "----"
                    }`,
                },
                {
                    title: "Synth’s demand forecast",
                    value: Math.round(gwmData?.totalForecastQty),
                },
            ],
            toolTipContent: {
                title: "Synth’s demand forecast",
                content:
                    "This is the total number of units across all your bundles that you are most likely going to sell over the next 6 months.",
            },
        },
        {
            title: "Forecasted inventory deficit",
            values: Math.round(gwmData?.forecastedStockNeeded) + " (OOS)",
            loader: gwmLoading,
            toolTipContent: {
                title: "Forecasted inventory deficit",
                content:
                    "This is the total number of units of all your products that are likely to be out of stock in a future period. These are the likely out of stock units of products with respect to the demand forecast.",
            },
        },
    ];

    return (
        <div className="py-[16px] px-[20px]">
            <LoadingContent loading={gwmLoading} renderLoading={() => <ChartSkeleton />}>
                {() => (
                    <div>
                        <div className="flex justify-between items-start w-full">
                            <div className="flex flex-wrap gap-3.5">
                                {[
                                    {
                                        title: "Current sales",
                                        icon: "https://res.cloudinary.com/dvxi7qcmd/image/upload/v1723568484/blue_line_yds7b9.svg",
                                    },
                                    {
                                        title: "My configured plan",
                                        icon: "https://res.cloudinary.com/dvxi7qcmd/image/upload/v1712800595/chartLine2_o41vii.svg",
                                    },
                                    {
                                        title: "Synth's predicted forecast",
                                        icon: "https://res.cloudinary.com/dvxi7qcmd/image/upload/v1723568484/red_line_yfjgnt.svg",
                                    },
                                    {
                                        title: "My saved target",
                                        icon: (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-4 h-4"
                                                fill="none"
                                                viewBox="0 0 16 15"
                                            >
                                                <path
                                                    stroke="#6562E5"
                                                    strokeDasharray="2 2"
                                                    strokeWidth="1.5"
                                                    d="M0 8.5h3.5l2.5-6 3.5 10 1.5-4h5"
                                                ></path>
                                            </svg>
                                        ),
                                    },
                                ].map((item) => (
                                    <div className="flex items-center">
                                        {typeof item.icon === "string" ? (
                                            <img
                                                src={item.icon}
                                                alt="this-trend"
                                                className="w-4 h-4"
                                            />
                                        ) : (
                                            item.icon
                                        )}
                                        <p className="text-slate-700 font-rocGroteskMedium text-[12px] leading-3 ml-1">
                                            {item.title}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 w-full mt-[16px]">
                            {forecastMetrics.map((item, index) => (
                                <div
                                    className={clsx(
                                        "py-2",
                                        index !== 0 && "border-l-2 border-l-slate-200 pl-3 "
                                    )}
                                >
                                    <p className="text-slate-400 font-rocGroteskMedium text-[10px] leading-3 ml-1 flex items-center gap-1  ">
                                        {item.title}{" "}
                                        <div>
                                            <a id={`tooltip-${index}`}>
                                                <i className="ri-information-fill text-slate-300 text-sm"></i>
                                            </a>
                                            <Tooltip
                                                anchorSelect={`#tooltip-${index}`}
                                                place={"top"}
                                                content={item.toolTipContent.content}
                                                style={{
                                                    maxWidth: "250px",
                                                    zIndex: 9999,
                                                    background: "black",
                                                    borderRadius: "6px",
                                                    border: "1px solid #E2E8F0",
                                                    lineHeight: "normal",
                                                    textAlign: "left",
                                                    color: "white",
                                                    boxShadow:
                                                        "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                                    fontSize: 12,
                                                }}
                                            />
                                        </div>
                                    </p>

                                    <div className="mt-[20px]">
                                        {Array.isArray(item.values) ? (
                                            <div className="grid grid-cols-2 gap-2">
                                                {item.values.map((value, index) => (
                                                    <div
                                                        key={index}
                                                        className={clsx(
                                                            index !== 0 &&
                                                                "border-l border-l-slate-200 pl-3"
                                                        )}
                                                    >
                                                        <p className="text-slate-700 font-rocGroteskMedium text-lg leading-3 ml-1">
                                                            {value.value}
                                                        </p>
                                                        <p className="text-slate-400 font-rocGroteskMedium text-[10px] leading-3 ml-1 mt-2">
                                                            {value.title}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-slate-700 font-rocGroteskMedium text-lg leading-3 ml-1">
                                                {item.values}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div>
                            {productData?.length !== 0 &&
                            gwmData?.currentSales?.length === 0 &&
                            gwmData?.forcastSales?.length === 0 ? (
                                <div className="w-[100%] h-[400px] flex items-center justify-center">
                                    <div className="flex items-center  gap-[3px] flex-col w-[353px] justify-center">
                                        <img
                                            src="https://res.cloudinary.com/dvxi7qcmd/image/upload/v1717426949/icon_w8c4mn.png"
                                            alt=""
                                            className=""
                                        />
                                        <p className="text-g-75 font-rocGroteskBold text-base ">
                                            {" "}
                                            No forecast data to show for this query{" "}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-20 mb-6">
                                    <MultipleLineChart mapData={chartData} />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </LoadingContent>
        </div>
    );
}

function ChartSkeleton() {
    return (
        <div className="w-full">
            <div className="flex flex-col gap-2 items-end pr-10">
                <Skeleton
                    height={10}
                    width={90}
                    highlightColor="#E2E8F0"
                    borderRadius={16}
                    baseColor="#F1F5F9"
                    className="custom-skeleton"
                />
                <Skeleton
                    height={10}
                    width={40}
                    highlightColor="#E2E8F0"
                    borderRadius={16}
                    baseColor="#F1F5F9"
                    className="custom-skeleton"
                />
                <Skeleton
                    height={10}
                    width={200}
                    highlightColor="#E2E8F0"
                    borderRadius={16}
                    baseColor="#F1F5F9"
                    className="custom-skeleton"
                />
            </div>

            <div className="mt-20">
                <div className="flex gap-10 mb-5">
                    <div className="flex flex-col gap-[17px]">
                        <span className="block text-[9px] text-slate-500">70k</span>
                        <span className="block text-[9px] text-slate-500">60k</span>
                        <span className="block text-[9px] text-slate-500">50k</span>
                        <span className="block text-[9px] text-slate-500">40k</span>
                        <span className="block text-[9px] text-slate-500">30k</span>
                        <span className="block text-[9px] text-slate-500">20k</span>
                        <span className="block text-[9px] text-slate-500">10k</span>
                    </div>

                    <div className="">
                        <svg
                            className="w-full h-48 skeleton-pulse"
                            viewBox="0 0 1000 200"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M0,150 C60,100 120,60 180,30 C240,10 280,20 320,40 C380,70 440,90 500,100 C560,110 600,100 640,80 C680,60 720,40 760,25 C800,10 850,5 900,0 C950,0 975,0 1000,0"
                                stroke="#E2E8F0"
                                stroke-width="6"
                                fill="none"
                            />
                        </svg>
                    </div>
                </div>

                <div className="flex items-center gap-2 justify-between pt-[15px] border-t border-slate-200 ml-10">
                    <span className="block text-[9px] text-slate-500">Jan</span>
                    <span className="block text-[9px] text-slate-500">Feb</span>
                    <span className="block text-[9px] text-slate-500">Mar</span>
                    <span className="block text-[9px] text-slate-500">Apr</span>
                    <span className="block text-[9px] text-slate-500">May</span>
                    <span className="block text-[9px] text-slate-500">Jun</span>
                </div>
            </div>
        </div>
    );
}
